// src/hooks/useNotifications.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { subscriptionRequestService } from '../services/apiServices';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchNotifications = async () => {
    if (!user || user.role !== 'superadmin') {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      // Fetch all subscription requests and filter pending ones
      const response = await subscriptionRequestService.getAllRequests();

      if (response.success) {
        const allRequests = response.data || [];
        
        // Filter only pending requests
        const pendingRequests = allRequests.filter(request => request.status === 'pending');
        
        // Transform subscription requests into notifications
        const notificationItems = pendingRequests.map(request => ({
          id: request._id,
          type: 'subscription_request',
          title: `Nouvelle demande d'abonnement`,
          message: `${request.companyName} demande un ${request.requestType}`,
          priority: request.priority,
          createdAt: request.createdAt,
          data: request,
          read: false
        }));

        setNotifications(notificationItems);
        setUnreadCount(notificationItems.length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Clear notifications on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  };

  const clearNotification = (notificationId) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
    setUnreadCount(prev => {
      const notification = notifications.find(n => n.id === notificationId);
      return notification && !notification.read ? Math.max(0, prev - 1) : prev;
    });
  };

  useEffect(() => {
    fetchNotifications();
    
    // Set up polling for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    // Listen for manual refresh events
    const handleRefresh = () => {
      fetchNotifications();
    };
    
    window.addEventListener('refreshNotifications', handleRefresh);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotifications', handleRefresh);
    };
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearNotification
  };
};